import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Goals from "@/pages/Goals";
import Deals from "@/pages/Deals";
import Planner from "@/pages/Planner";
import Community from "@/pages/Community";
import Wallet from "@/pages/Wallet";
import Settings from "@/pages/Settings";
import Reservations from "@/pages/Reservations";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Marketplace from "@/pages/Marketplace";
import Admin from "@/pages/Admin";

// Components
import ProtectedRoute from "@/components/ProtectedRoute";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import CookieConsent from "@/components/CookieConsent";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const isDev = process.env.NODE_ENV !== "production";

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        if (isDev) console.log('ServiceWorker registered:', registration.scope);
      })
      .catch((error) => {
        if (isDev) console.log('ServiceWorker registration failed:', error);
      });
  });
}

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id in hash OR in full URL
  // This handles cases where hash might be in window.location but not in location object
  const hasSessionId = location.hash?.includes('session_id=') || 
                       window.location.hash?.includes('session_id=') ||
                       window.location.href?.includes('session_id=');
  
  if (hasSessionId) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/goals" element={
        <ProtectedRoute>
          <Goals />
        </ProtectedRoute>
      } />
      <Route path="/deals" element={
        <ProtectedRoute>
          <Deals />
        </ProtectedRoute>
      } />
      <Route path="/planner" element={
        <ProtectedRoute>
          <Planner />
        </ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute>
          <Community />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          <Wallet />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/reservations" element={
        <ProtectedRoute>
          <Reservations />
        </ProtectedRoute>
      } />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Admin />
        </ProtectedRoute>
      } />
      <Route path="/auth" element={<Auth />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
    </Routes>
  );
}

function App() {
  // Initialize theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("savecation_theme") || "light";
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-slate-50">
        <BrowserRouter>
          <AppRouter />
          <CookieConsent />
          <PWAInstallPrompt />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
